import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Modal } from "@mantine/core";
import schedulerService from "../../../services/scheduler";
import { fmtRange, isoDate, addDays, startOfDay, sameDay, DOW, MON_SHORT } from "./scheduleUtils";
import "./UpcomingAppointments.css";

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);

// How far ahead to look for upcoming appointments.
const WINDOW_DAYS = 45;

const CalIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const dayHeading = (d) => {
  const now = new Date();
  if (sameDay(d, addDays(now, 1))) return "Tomorrow";
  return `${DOW[d.getDay()]}, ${MON_SHORT[d.getMonth()]} ${d.getDate()}`;
};

const groupByDay = (list) => {
  const out = [];
  const seen = new Map();
  list.forEach((a) => {
    const key = isoDate(new Date(a.date));
    if (!seen.has(key)) { seen.set(key, { date: new Date(a.date), rows: [] }); out.push(seen.get(key)); }
    seen.get(key).rows.push(a);
  });
  return out;
};

// One appointment line. `context` ('client' | 'facility') decides the secondary label.
function ApptRow({ a, context, today }) {
  const time = a.allDay ? "All day" : fmtRange(a.startMin, a.endMin);
  const title =
    a.apptTitle ||
    (a.type === "facility" ? a.facility?.name || "Facility appointment" : a.clientName || a.client?.name || "Client appointment");
  const meta =
    context === "facility"
      ? a.type === "facility" ? "Facility-wide" : a.clientName || a.client?.name || "Client"
      : a.facility?.name || "";
  return (
    <div className={`ua-row ${today ? "ua-row-today" : ""}`}>
      <span className={`ua-dot ${a.type === "facility" ? "ua-dot-fac" : "ua-dot-cli"}`} />
      <div className="ua-row-main">
        <div className="ua-row-title">{title}</div>
        {meta ? <div className="ua-row-meta">{meta}</div> : null}
      </div>
      <div className="ua-row-time">{time}</div>
    </div>
  );
}

// Today block + upcoming day-groups. Shared by the inline panel and the modal.
function Blocks({ todays, groups, context }) {
  return (
    <>
      {todays.length > 0 ? (
        <div className="ua-today">
          <div className="ua-today-label">
            <span className="ua-today-dot" /> Today · {moment().format("ddd, MMM D")}
          </div>
          {todays.map((a) => <ApptRow key={idOf(a)} a={a} context={context} today />)}
        </div>
      ) : null}
      {groups.length > 0 ? (
        <div className="ua-upcoming">
          {todays.length > 0 ? <div className="ua-upcoming-label">Upcoming</div> : null}
          {groups.map((g) => (
            <div key={isoDate(g.date)} className="ua-group">
              <div className="ua-group-date">{dayHeading(g.date)}</div>
              {g.rows.map((a) => <ApptRow key={idOf(a)} a={a} context={context} />)}
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

// Compact "Appointments" panel for the client facesheet and facility pages.
// Highlights today's appointments and lists upcoming ones. Caps the inline view
// and opens the full list (today + all upcoming) in a popup via "+ More…".
// Pass exactly one of clientId / facilityId.
export default function UpcomingAppointments({ clientId, facilityId, all = false, title = "Appointments", compact = false, onAdd }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const context = facilityId ? "facility" : "client";
  const inlineMax = compact ? 2 : 6;

  useEffect(() => {
    if (!clientId && !facilityId && !all) return undefined;
    let cancelled = false;
    setLoading(true);
    const params = {
      kind: "appointment",
      from: isoDate(new Date()),
      to: isoDate(addDays(new Date(), WINDOW_DAYS)),
      ...(clientId ? { client: clientId } : facilityId ? { facility: facilityId } : {}),
    };
    schedulerService
      .getAppointments(params)
      .then((arr) => {
        if (cancelled) return;
        const sorted = (arr || [])
          .filter((a) => a && a.date)
          .sort((x, y) => new Date(x.date) - new Date(y.date) || (x.startMin || 0) - (y.startMin || 0));
        setItems(sorted);
        setLoading(false);
      })
      .catch(() => !cancelled && (setItems([]), setLoading(false)));
    return () => { cancelled = true; };
  }, [clientId, facilityId, all]);

  const { todays, futureFlat } = useMemo(() => {
    const now = new Date();
    const today = [];
    const future = [];
    items.forEach((a) => {
      const d = new Date(a.date);
      if (sameDay(d, now)) today.push(a);
      else if (startOfDay(d) > startOfDay(now)) future.push(a);
    });
    return { todays: today, futureFlat: future };
  }, [items]);

  const total = todays.length + futureFlat.length;

  // Inline (capped) view — at most `inlineMax` rows, today first.
  const todaysShown = todays.slice(0, inlineMax);
  const upSlots = Math.max(0, inlineMax - todaysShown.length);
  const upcomingShownFlat = futureFlat.slice(0, upSlots);
  const upcomingShown = groupByDay(upcomingShownFlat);
  const moreCount = total - (todaysShown.length + upcomingShownFlat.length);

  const hasAny = total > 0;

  return (
    <section className={`ua-card ${compact ? "ua-compact" : ""}`} aria-label={title}>
      <header className="ua-head">
        <span className="ua-head-ic"><CalIcon width={16} height={16} /></span>
        <h3 className="ua-head-title">{title}</h3>
        <span className="ua-head-spacer" />
        {todays.length > 0 ? (
          <span className="ua-head-pill">{todays.length} today</span>
        ) : !loading && total > 0 ? (
          <span className="ua-head-sub">{total} upcoming</span>
        ) : null}
        {onAdd ? (
          <button type="button" className="ua-add-btn" onClick={onAdd} title="Add appointment" aria-label="Add appointment">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        ) : null}
      </header>

      <div className="ua-body">
        {loading ? (
          <div className="ua-empty">Loading appointments…</div>
        ) : !hasAny ? (
          <div className="ua-empty">No appointments scheduled.</div>
        ) : (
          <>
            <Blocks todays={todaysShown} groups={upcomingShown} context={context} />
            {moreCount > 0 ? (
              <button type="button" className="ua-more-btn" onClick={() => setShowAll(true)}>
                + {moreCount} more…
              </button>
            ) : null}
          </>
        )}
      </div>

      <Modal
        opened={showAll}
        onClose={() => setShowAll(false)}
        title={title}
        centered
        radius={14}
        size={460}
        zIndex={1000}
        overlayProps={{ opacity: 0.45, blur: 1 }}
        styles={{ title: { fontWeight: 700, color: "var(--app-text)" }, body: { paddingTop: 6 } }}
      >
        <div className="ua-modal-body">
          <Blocks todays={todays} groups={groupByDay(futureFlat)} context={context} />
        </div>
      </Modal>
    </section>
  );
}
